/**
 * A máquina de arcade da Sala de Descanso — o protocolo.
 *
 * O MOTOR vive no `@greenvoice/shared` (`rush.ts`), puro e determinístico. O que
 * está aqui é a outra metade: quem está ao volante, quem está na fila, e como é
 * que seis ecrãs vêem a MESMA corrida sem haver servidor a dizer quem tem razão.
 *
 * ── A decisão de fundo: não se transmite o jogo, transmite-se o volante ──────
 *
 * O caminho óbvio era mandar o estado — a carrinha aqui, os obstáculos ali —
 * dez vezes por segundo. Não é o que está aqui, e a diferença não é de gosto:
 *
 *  • Um estado a 10 Hz obriga o espectador a INVENTAR os 5 fotogramas do meio.
 *    Mandar o volante deixa cada ecrã correr o motor a 60 fps de verdade.
 *  • Uma pontuação transmitida é uma AFIRMAÇÃO — alguém diz que fez 12 400 e o
 *    resto acredita. Aqui ninguém anuncia nada: cada ecrã corre a mesma
 *    simulação a partir dos mesmos comandos e chega a 12 400 sozinho. Um recorde
 *    que não precisa de ser acreditado não precisa de ser defendido.
 *  • São menos bytes por ordens de grandeza. Um pacote quando o volante MUDA (e
 *    um sinal de vida de vez em quando), não sessenta estados por segundo.
 *
 * ── As três coisas que isto obriga ──────────────────────────────────────────
 *
 *  1. A FILA É COMUTATIVA. Ordena-se por (corrida em que se entrou, identidade),
 *     nunca por ordem de chegada dos pacotes — que é diferente em cada ecrã.
 *     Dois ecrãs com a fila por ordens diferentes apontavam o volante a pessoas
 *     diferentes, e a sala partia-se ao meio.
 *  2. NINGUÉM SIMULA À FRENTE DO QUE ESTÁ CONFIRMADO. O espectador anda ~100 ms
 *     atrás e só avança até `upto` — o tick que o piloto já garantiu. Avançar
 *     mais depressa era adivinhar que o volante não mudou, e uma adivinhação
 *     errada não se corrige: a corrida a partir dali fica OUTRA.
 *  3. UM PACOTE PERDIDO TEM DE SE NOTAR. Cada mensagem traz `n`, o número de
 *     mudanças que o piloto já mandou. Se não bater certo com o que se tem, o
 *     estado congela e pede-se `r:hello` — em vez de continuar a simular uma
 *     corrida que já não é a mesma, de cara séria.
 *
 * O fim da corrida é a única coisa que viaja como facto (`r:end`), e mesmo essa
 * traz só o TICK: a pontuação continua a ser derivada em cada ecrã.
 */

import {
  RUSH_TPS,
  type RushDir,
  type RushKind,
  type RushState as RushSim,
  rushMeters,
  rushSeed,
  rushStart,
  rushStep,
} from "@greenvoice/shared";

/** Uma corrida não passa daqui. Um `t` absurdo num pacote não pode pôr um ecrã a
 *  correr uma simulação de mil milhões de passos. */
export const R_MAX_TICK = RUSH_TPS * 60 * 5;
/** Nem uma "corrida" que seja só um piloto a mudar de direcção sessenta vezes
 *  por segundo durante cinco minutos. */
export const R_MAX_CHANGES = 6000;
/** De quantos em quantos ticks o piloto dá sinal de vida. A 6 ticks são 10 por
 *  segundo — chega para o espectador nunca ficar mais de 100 ms parado. */
export const R_BEAT = 6;

/** Um lugar na fila. O `at` é a corrida em que se entrou, e é ele que faz a
 *  ordenação ser independente da ordem por que os pacotes chegam. */
export interface RushSeat {
  readonly id: string;
  readonly at: number;
}

/** O volante mudou no tick `t` para a direcção `d`, e assim fica até à próxima. */
export interface RushChange {
  readonly t: number;
  readonly d: RushDir;
}

export interface RushBest {
  readonly who: string;
  readonly m: number;
  readonly run: number;
}

export interface RushLast {
  readonly who: string;
  readonly m: number;
  readonly cans: number;
  readonly hit: RushKind | -1;
  readonly run: number;
  /** Bateu o recorde da sala. Guardado porque o `best` já foi actualizado e
   *  depois disso não dava para saber. */
  readonly record: boolean;
}

export interface RushRoom {
  /** Quem quer jogar, por (corrida de entrada, identidade). A cabeça é a vez. */
  readonly queue: readonly RushSeat[];
  /** Quantas corridas já acabaram. É metade da semente. */
  readonly run: number;
  /** Quem está ao volante AGORA, ou null. Fica congelado durante a corrida: se
   *  fosse sempre a cabeça da fila, uma saída a meio mudava a semente e a
   *  estrada mudava debaixo dos pés a quem estava a jogar. */
  readonly pilot: string | null;
  /** As mudanças de volante desta corrida. É ISTO o estado partilhado. */
  readonly changes: readonly RushChange[];
  /** O tick até onde é seguro simular. */
  readonly upto: number;
  /** Verdadeiro quando se detectou um buraco no histórico: pára-se tudo e
   *  pede-se `r:hello`, porque simular por cima de um buraco dá outra corrida. */
  readonly gap: boolean;
  readonly best: RushBest | null;
  readonly last: RushLast | null;
}

export type RMsg =
  | { k: "r:hello"; by: string }
  | {
      k: "r:sync";
      to: string;
      queue: RushSeat[];
      run: number;
      pilot: string | null;
      changes: RushChange[];
      upto: number;
      best: RushBest | null;
      last: RushLast | null;
    }
  | { k: "r:join"; by: string; at: number }
  | { k: "r:quit"; by: string }
  | { k: "r:go"; by: string; run: number }
  | { k: "r:in"; by: string; run: number; n: number; t: number; d: RushDir }
  | { k: "r:beat"; by: string; run: number; n: number; t: number }
  | { k: "r:end"; by: string; run: number; n: number; t: number; m: number };

export function rEmpty(): RushRoom {
  return {
    queue: [],
    run: 0,
    pilot: null,
    changes: [],
    upto: 0,
    gap: false,
    best: null,
    last: null,
  };
}

// ── Leituras ────────────────────────────────────────────────

/** Quem tem a vez. Durante a corrida é o piloto congelado. */
export function rHead(state: RushRoom): string | null {
  return state.pilot ?? state.queue[0]?.id ?? null;
}

export function rLive(state: RushRoom): boolean {
  return state.pilot !== null;
}

/** A semente desta corrida. Ninguém a transmite: toda a gente a calcula. */
export function rSeed(state: RushRoom): number {
  return rushSeed(state.run, rHead(state) ?? "");
}

/** O lugar de `id` na fila, a contar de 1. Zero quer dizer que não está. */
export function rPlace(state: RushRoom, id: string | null): number {
  if (!id) return 0;
  const i = state.queue.findIndex((s) => s.id === id);
  return i < 0 ? 0 : i + 1;
}

/** A direcção em vigor no tick `t`. Vale até à mudança seguinte — é por isso que
 *  chega mandar um pacote quando o volante MUDA. */
export function rDirAt(changes: readonly RushChange[], t: number): RushDir {
  let d: RushDir = 0;
  for (const c of changes) {
    if (c.t > t) break;
    d = c.d;
  }
  return d;
}

/** Corre o motor do princípio até `t`. Determinístico: dá o mesmo em todo o
 *  lado, que é a razão de existir de todo este ficheiro.
 *
 *  Anda com um cursor sobre as mudanças em vez de perguntar `rDirAt` a cada
 *  tick: a lista pode ter milhares de entradas e uma procura por tick tornava
 *  isto quadrático — num sítio que corre a cada fim de corrida. */
export function rSim(seed: number, changes: readonly RushChange[], t: number): RushSim {
  let s = rushStart(seed);
  const fim = Math.min(t, R_MAX_TICK);
  let i = 0;
  let d: RushDir = 0;
  for (let tick = 1; tick <= fim && !s.over; tick += 1) {
    while (i < changes.length && (changes[i]?.t ?? Number.POSITIVE_INFINITY) <= tick) {
      d = changes[i]?.d ?? d;
      i += 1;
    }
    s = rushStep(s, d);
  }
  return s;
}

// ── O reducer ───────────────────────────────────────────────

/**
 * Aplica uma mensagem. Devolve o MESMO estado quando nada muda — pacote
 * repetido, comando de quem não está ao volante, corrida que já acabou.
 */
export function rReduce(state: RushRoom, m: RMsg): RushRoom {
  switch (m.k) {
    case "r:join": {
      if (!m.by || state.queue.some((s) => s.id === m.by)) return state;
      // O `at` vem do pacote tal e qual. Podia parecer mais seguro forçá-lo ao
      // `run` local — e seria o contrário: dois ecrãs em contagens diferentes
      // davam ao mesmo lugar `at` diferentes, e a fila deixava de ser a mesma.
      // O valor tem UM autor, por isso chega a todos igual.
      const at = Math.max(0, Math.min(R_MAX_TICK, Math.trunc(m.at)));
      return { ...state, queue: ordena([...state.queue, { id: m.by, at }]) };
    }

    case "r:quit": {
      if (!m.by || !state.queue.some((s) => s.id === m.by)) return state;
      const queue = state.queue.filter((s) => s.id !== m.by);
      // Sair a meio da própria corrida acaba-a — mas pela pontuação que já
      // estava feita, não a zero. Desistir não é bater.
      if (state.pilot === m.by) return fecha({ ...state, queue }, state.upto, null);
      return { ...state, queue };
    }

    case "r:go": {
      // O número da corrida é o que torna um duplo clique inerte: o segundo traz
      // a corrida que o primeiro já começou.
      if (rLive(state) || m.run !== state.run) return state;
      if (!m.by || state.queue[0]?.id !== m.by) return state;
      return { ...state, pilot: m.by, changes: [], upto: 0, gap: false, last: null };
    }

    case "r:in": {
      if (!aoVolante(state, m.by, m.run)) return state;
      if (state.changes.length >= R_MAX_CHANGES) return state;
      const t = Math.trunc(m.t);
      if (!Number.isInteger(t) || t < 1 || t > R_MAX_TICK) return state;
      // Estritamente crescente: um pacote fora de ordem reescreveria um pedaço
      // de corrida que os ecrãs já simularam, e não há como desfazer isso.
      if (t <= (state.changes[state.changes.length - 1]?.t ?? 0)) return state;
      if (m.d !== -1 && m.d !== 0 && m.d !== 1) return state;
      // `n` é o detector de buracos: se não bate certo, falta um pacote pelo
      // meio e continuar era simular uma corrida diferente com ar de certa.
      if (m.n !== state.changes.length) return state.gap ? state : { ...state, gap: true };
      return {
        ...state,
        changes: [...state.changes, { t, d: m.d }],
        upto: Math.max(state.upto, t),
        gap: false,
      };
    }

    case "r:beat": {
      if (!aoVolante(state, m.by, m.run)) return state;
      const t = Math.trunc(m.t);
      if (!Number.isInteger(t) || t < 1 || t > R_MAX_TICK) return state;
      if (m.n !== state.changes.length) return state.gap ? state : { ...state, gap: true };
      // O sinal de vida é o que diz "até aqui o volante foi mesmo o que sabes".
      // Sem ele, um piloto a segurar a direcção durante três segundos deixava os
      // espectadores parados a olhar, sem forma de saber se era calma ou queda.
      return { ...state, upto: Math.max(state.upto, t), gap: false };
    }

    case "r:end": {
      if (!aoVolante(state, m.by, m.run)) return state;
      const t = Math.max(state.upto, Math.min(R_MAX_TICK, Math.trunc(m.t) || 0));
      // Com o histórico completo, a pontuação DERIVA-SE aqui e o número que veio
      // no pacote é ignorado. Só se faltar um pacote é que se aceita o que o
      // piloto diz — e nesse caso o `r:sync` seguinte corrige.
      const fiavel = m.n === state.changes.length && !state.gap;
      return fecha(state, t, fiavel ? null : m.m);
    }

    case "r:sync": {
      // Vários presentes respondem ao mesmo `hello`. Fica o mais ADIANTADO:
      // primeiro a corrida mais alta, depois o histórico mais longo. Nenhum dos
      // dois recua, por isso um `sync` atrasado nunca desfaz o que já se sabe.
      const virgem = state.run === 0 && !rLive(state) && state.queue.length === 0 && state.best === null;
      const adiante =
        m.run > state.run || (m.run === state.run && (m.changes.length > state.changes.length || m.upto > state.upto));
      if (!adiante && !virgem) return state;
      const changes = limpaChanges(m.changes);
      return {
        queue: ordena(m.queue),
        run: Math.max(0, Math.trunc(m.run) || 0),
        pilot: m.pilot || null,
        changes,
        upto: Math.max(0, Math.min(R_MAX_TICK, Math.trunc(m.upto) || 0)),
        gap: false,
        best: m.best,
        last: m.last,
      };
    }

    case "r:hello":
      // Quem responde é o componente — é ele que sabe enviar. Aqui nada muda.
      return state;
  }
}

function aoVolante(state: RushRoom, by: string, run: number): boolean {
  return rLive(state) && state.pilot === by && run === state.run;
}

/** A fila, por (corrida de entrada, identidade). Ordenar por identidade em
 *  empate é o que faz duas chegadas simultâneas darem a mesma fila nos dois
 *  ecrãs — sem isso era preciso um árbitro, e não há. */
function ordena(seats: readonly RushSeat[]): RushSeat[] {
  const vistos = new Map<string, RushSeat>();
  for (const s of seats) {
    if (!s.id || vistos.has(s.id)) continue;
    vistos.set(s.id, { id: s.id, at: Math.max(0, Math.trunc(s.at) || 0) });
  }
  return [...vistos.values()].sort((a, b) => a.at - b.at || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

function limpaChanges(cs: readonly RushChange[]): RushChange[] {
  const out: RushChange[] = [];
  for (const c of cs) {
    const t = Math.trunc(c.t);
    if (!Number.isInteger(t) || t < 1 || t > R_MAX_TICK) continue;
    if (t <= (out[out.length - 1]?.t ?? 0)) continue;
    if (c.d !== -1 && c.d !== 0 && c.d !== 1) continue;
    out.push({ t, d: c.d });
    if (out.length >= R_MAX_CHANGES) break;
  }
  return out;
}

/**
 * Fecha a corrida: apura o resultado, actualiza o recorde, passa a vez.
 *
 * Nenhuma destas três coisas precisa de pacote. A pontuação sai da simulação,
 * que é igual em todo o lado; o recorde sai da pontuação; e a vez sai da fila,
 * que também já é igual em todo o lado. É a propriedade que justifica o desenho
 * inteiro — o que viaja é o volante, o resto DEDUZ-SE.
 */
function fecha(state: RushRoom, t: number, metrosDitos: number | null): RushRoom {
  const quem = state.pilot;
  if (!quem) return state;
  const fim = rSim(rushSeed(state.run, quem), state.changes, t);
  const m = metrosDitos === null ? rushMeters(fim.score) : Math.max(0, Math.trunc(metrosDitos) || 0);
  const record = m > (state.best?.m ?? 0);
  return {
    queue: state.queue.filter((s) => s.id !== quem),
    run: state.run + 1,
    pilot: null,
    changes: [],
    upto: 0,
    gap: false,
    best: record ? { who: quem, m, run: state.run } : state.best,
    last: { who: quem, m, cans: fim.cans, hit: fim.hit, run: state.run, record },
  };
}

/**
 * Tira da fila quem já não está na call.
 *
 * A lista de presentes vem do SFU e é igual em todos os ecrãs, por isso esta
 * limpeza dá o mesmo resultado em todos — não é preciso pacote nenhum. Se quem
 * desapareceu era o piloto, a corrida fecha-se com o que já estava feito: a
 * máquina não pode ficar presa a alguém que fechou o portátil.
 */
export function rPrune(state: RushRoom, present: ReadonlySet<string>): RushRoom {
  const queue = state.queue.filter((s) => present.has(s.id));
  if (state.pilot && !present.has(state.pilot)) return fecha({ ...state, queue }, state.upto, null);
  if (queue.length === state.queue.length) return state;
  return { ...state, queue };
}

// ── Pacotes ─────────────────────────────────────────────────

function num(v: unknown): number {
  return typeof v === "number" && Number.isFinite(v) ? Math.trunc(v) : Number.NaN;
}

function dir(v: unknown): RushDir | null {
  return v === -1 || v === 0 || v === 1 ? v : null;
}

function seats(v: unknown): RushSeat[] {
  if (!Array.isArray(v)) return [];
  const out: RushSeat[] = [];
  for (const s of v) {
    if (typeof s !== "object" || s === null) continue;
    const o = s as Record<string, unknown>;
    const id = typeof o["id"] === "string" ? o["id"] : "";
    const at = num(o["at"]);
    if (id && Number.isInteger(at)) out.push({ id, at });
  }
  return out;
}

function changes(v: unknown): RushChange[] {
  if (!Array.isArray(v)) return [];
  const out: RushChange[] = [];
  for (const c of v) {
    if (typeof c !== "object" || c === null) continue;
    const o = c as Record<string, unknown>;
    const t = num(o["t"]);
    const d = dir(o["d"]);
    if (Number.isInteger(t) && d !== null) out.push({ t, d });
  }
  return out;
}

function best(v: unknown): RushBest | null {
  if (typeof v !== "object" || v === null) return null;
  const o = v as Record<string, unknown>;
  const who = typeof o["who"] === "string" ? o["who"] : "";
  const m = num(o["m"]);
  const run = num(o["run"]);
  if (!who || !Number.isInteger(m) || m < 0) return null;
  return { who, m, run: Number.isInteger(run) ? run : 0 };
}

function last(v: unknown): RushLast | null {
  if (typeof v !== "object" || v === null) return null;
  const o = v as Record<string, unknown>;
  const who = typeof o["who"] === "string" ? o["who"] : "";
  const m = num(o["m"]);
  if (!who || !Number.isInteger(m) || m < 0) return null;
  const h = num(o["hit"]);
  return {
    who,
    m,
    cans: Math.max(0, num(o["cans"]) || 0),
    hit: h === 0 || h === 1 || h === 2 || h === 3 ? (h as RushKind) : -1,
    run: Math.max(0, num(o["run"]) || 0),
    record: o["record"] === true,
  };
}

/**
 * Valida um pacote cru do canal de dados.
 *
 * A call é território partilhado: um pacote malformado — de um cliente velho, de
 * um bug, de alguém a brincar com a consola — não pode entrar como `any` no
 * estado do jogo nem pôr o motor a correr com números impossíveis.
 */
export function rParse(data: unknown): RMsg | null {
  if (typeof data !== "object" || data === null) return null;
  const m = data as Record<string, unknown>;
  switch (m["k"]) {
    case "r:hello":
      return { k: "r:hello", by: String(m["by"] ?? "") };
    case "r:sync":
      return {
        k: "r:sync",
        to: String(m["to"] ?? ""),
        queue: seats(m["queue"]),
        run: Math.max(0, num(m["run"]) || 0),
        pilot: typeof m["pilot"] === "string" && m["pilot"] ? m["pilot"] : null,
        changes: changes(m["changes"]),
        upto: Math.max(0, num(m["upto"]) || 0),
        best: best(m["best"]),
        last: last(m["last"]),
      };
    case "r:join":
      return { k: "r:join", by: String(m["by"] ?? ""), at: num(m["at"]) };
    case "r:quit":
      return { k: "r:quit", by: String(m["by"] ?? "") };
    case "r:go":
      return { k: "r:go", by: String(m["by"] ?? ""), run: num(m["run"]) };
    case "r:in": {
      const d = dir(m["d"]);
      if (d === null) return null;
      return {
        k: "r:in",
        by: String(m["by"] ?? ""),
        run: num(m["run"]),
        n: num(m["n"]),
        t: num(m["t"]),
        d,
      };
    }
    case "r:beat":
      return {
        k: "r:beat",
        by: String(m["by"] ?? ""),
        run: num(m["run"]),
        n: num(m["n"]),
        t: num(m["t"]),
      };
    case "r:end":
      return {
        k: "r:end",
        by: String(m["by"] ?? ""),
        run: num(m["run"]),
        n: num(m["n"]),
        t: num(m["t"]),
        m: num(m["m"]),
      };
    default:
      return null;
  }
}

/** O estado inteiro, para responder a um `r:hello`. */
export function rSyncMsg(state: RushRoom, to: string): RMsg {
  return {
    k: "r:sync",
    to,
    queue: [...state.queue],
    run: state.run,
    pilot: state.pilot,
    changes: [...state.changes],
    upto: state.upto,
    best: state.best,
    last: state.last,
  };
}
